import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Checkbox,
  ListItemText,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Plus,
  Trash2,
  Upload,
  Search,
  Clock,
  Ban,
  Star,
  Edit2,
  X,
  Building2,
  CheckCircle2,
  PowerOff,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  fetchMarketingContacts,
  createMarketingContact,
  updateMarketingContact,
  deleteMarketingContact,
  importMarketingContacts,
  fetchMarketingSegments,
  fetchContactTimeline,
} from "../../../../../../redux/actions/marketingAction";
import { backendClient } from "../../../../../../api/backend";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const ContactList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { contacts, segments, timeline, loading } = useSelector((state) => state.marketing);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [segmentFilter, setSegmentFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | "active" | "inactive" | "unsubscribed"
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: "", first_name: "", last_name: "", company: "", team: "", phone: "", segment_ids: [], marketing_organization_id: null,
  });
  const fileInputRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const orgSearchTimer = useRef(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [editSelectedOrg, setEditSelectedOrg] = useState(null);

  const handleOrgSearch = useCallback((inputValue) => {
    if (orgSearchTimer.current) clearTimeout(orgSearchTimer.current);
    if (!inputValue || inputValue.length < 2) {
      setOrgSearchResults([]);
      return;
    }
    setOrgSearchLoading(true);
    orgSearchTimer.current = setTimeout(async () => {
      try {
        const data = await backendClient.getMarketingOrganizations({ search: inputValue });
        setOrgSearchResults(data.items || data);
      } catch {
        setOrgSearchResults([]);
      } finally {
        setOrgSearchLoading(false);
      }
    }, 300);
  }, []);

  useEffect(() => {
    const params = { page: page + 1, page_size: 25 };
    if (search) params.search = search;
    if (segmentFilter) params.segment_id = segmentFilter;
    if (teamFilter) params.team = teamFilter;
    if (activeFilter === "active") params.is_active = true;
    if (activeFilter === "inactive") params.is_active = false;
    if (activeFilter === "unsubscribed") params.unsubscribed = true;
    dispatch(fetchMarketingContacts(params));
    dispatch(fetchMarketingSegments());
  }, [dispatch, page, search, segmentFilter, teamFilter, activeFilter]);

  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");

  const handleCreate = async () => {
    setSubmitting(true);
    setCreateError("");
    try {
      await dispatch(createMarketingContact(formData));
      setCreateOpen(false);
      setSelectedOrg(null);
      setFormData({ email: "", first_name: "", last_name: "", company: "", team: "", phone: "", segment_ids: [], marketing_organization_id: null });
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("already exists")) {
        setCreateError(t("mkt_contact_email_already_exists"));
      } else {
        setCreateError(msg || t("mkt_contact_create_error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await dispatch(importMarketingContacts(file));
      setImportOpen(false);
    }
  };

  const handleRowClick = (contact) => {
    setSelectedContact(contact);
    setEditData({
      email: contact.email || "",
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      company: contact.company || "",
      team: contact.team || "",
      phone: contact.phone || "",
      segment_ids: (contact.segments || []).map((s) => s.id),
      marketing_organization_id: contact.marketing_organization_id || null,
      is_active: contact.is_active !== false,
    });
    setEditSelectedOrg(
      contact.marketing_organization_id
        ? { id: contact.marketing_organization_id, name: contact.organization_name }
        : null
    );
    setEditing(false);
    setDetailOpen(true);
  };

  const handleQuickToggleActive = async (contact, e) => {
    e.stopPropagation();
    const next = !(contact.is_active !== false);
    try {
      await dispatch(updateMarketingContact(contact.id, { is_active: next }));
      // refresh selected contact in dialog if open
      if (selectedContact?.id === contact.id) {
        setSelectedContact({ ...selectedContact, is_active: next });
      }
    } catch (err) {
      // noop — error surfaced via redux
    }
  };

  const handleSaveContact = async () => {
    setSaving(true);
    try {
      await dispatch(updateMarketingContact(selectedContact.id, editData));
      setDetailOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleShowTimeline = async (contact) => {
    setSelectedContact(contact);
    await dispatch(fetchContactTimeline(contact.id));
    setTimelineOpen(true);
  };

  const items = contacts?.items || [];
  const total = contacts?.total || 0;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t('mkt_contacts')} ({total})
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Upload size={16} />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            {t('mkt_import')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleImport}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => { setCreateError(""); setSelectedOrg(null); setOrgSearchResults([]); setCreateOpen(true); }}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            {t('mkt_add_contact')}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          placeholder={t('mkt_search_contacts')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          size="small"
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search size={16} color={theme.palette.text.secondary} /></InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>{t('mkt_segment')}</InputLabel>
          <Select value={segmentFilter} onChange={(e) => { setSegmentFilter(e.target.value); setPage(0); }} label={t('mkt_segment')}>
            <MenuItem value="">{t('mkt_all_segments')}</MenuItem>
            {(segments || []).map((seg) => (
              <MenuItem key={seg.id} value={seg.id}>{seg.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          placeholder={t('mkt_team') || "Team"}
          value={teamFilter}
          onChange={(e) => { setTeamFilter(e.target.value); setPage(0); }}
          size="small"
          sx={{ width: 180 }}
        />
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>{t("mkt_activation")}</InputLabel>
          <Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(0); }} label={t("mkt_activation")}>
            <MenuItem value="all">{t("mkt_activation_filter_all")}</MenuItem>
            <MenuItem value="active">{t("mkt_activation_filter_active")}</MenuItem>
            <MenuItem value="inactive">{t("mkt_activation_filter_inactive")}</MenuItem>
            <MenuItem value="unsubscribed">{t("mkt_activation_filter_unsubscribed")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_email')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('common_name')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_organisation')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_team')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('analytics_score')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_segments')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>{t('mkt_no_contacts')}</TableCell></TableRow>
              ) : items.map((contact) => {
                const contactActive = contact.is_active !== false;
                return (
                <TableRow
                  key={contact.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    opacity: contactActive && !contact.unsubscribed ? 1 : 0.6,
                  }}
                  onClick={() => handleRowClick(contact)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{contact.email}</Typography>
                  </TableCell>
                  <TableCell>{[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell>
                    {contact.organization_name ? (
                      <Chip
                        icon={<Building2 size={12} />}
                        label={contact.organization_name}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/marketing/organisations/${contact.marketing_organization_id}`);
                        }}
                        sx={{
                          borderRadius: "6px", fontSize: "0.7rem", height: 22, fontWeight: 500, cursor: "pointer",
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.main,
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                        }}
                      />
                    ) : (
                      contact.company || "\u2014"
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {contact.team || "\u2014"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<Star size={12} />}
                      label={contact.lead_score}
                      size="small"
                      sx={{
                        borderRadius: "6px",
                        fontSize: "0.7rem",
                        height: 22,
                        fontWeight: 600,
                        bgcolor: contact.lead_score >= 50
                          ? alpha("#10b981", 0.1)
                          : contact.lead_score >= 20
                          ? alpha("#f59e0b", 0.1)
                          : alpha(theme.palette.divider, 0.5),
                        color: contact.lead_score >= 50
                          ? "#10b981"
                          : contact.lead_score >= 20
                          ? "#f59e0b"
                          : theme.palette.text.secondary,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {contact.unsubscribed ? (
                      <Chip
                        icon={<Ban size={12} />}
                        label={t('mkt_unsubscribed')}
                        size="small"
                        color="error"
                        sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }}
                      />
                    ) : !contactActive ? (
                      <Chip
                        icon={<PowerOff size={11} />}
                        label={t("mkt_deactivated")}
                        size="small"
                        sx={{
                          borderRadius: "6px", fontSize: "0.65rem", height: 20, fontWeight: 600,
                          bgcolor: alpha(theme.palette.text.disabled, 0.15),
                          color: theme.palette.text.disabled,
                          "& .MuiChip-icon": { color: theme.palette.text.disabled },
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<CheckCircle2 size={11} />}
                        label={t('mkt_active')}
                        size="small"
                        color="success"
                        sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {(contact.segments || []).map((seg) => (
                        <Chip
                          key={seg.id}
                          label={seg.name}
                          size="small"
                          sx={{
                            borderRadius: "6px", fontSize: "0.6rem", height: 18,
                            bgcolor: seg.color ? alpha(seg.color, 0.1) : alpha(theme.palette.primary.main, 0.1),
                            color: seg.color || theme.palette.primary.main,
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={contactActive ? t("mkt_deactivate_action") : t("mkt_reactivate_action")}>
                        <IconButton size="small" onClick={(e) => handleQuickToggleActive(contact, e)}>
                          {contactActive
                            ? <PowerOff size={14} color={theme.palette.warning?.main} />
                            : <CheckCircle2 size={14} color={theme.palette.success?.main} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Timeline">
                        <IconButton size="small" onClick={() => handleShowTimeline(contact)}>
                          <Clock size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget(contact)}>
                          <Trash2 size={14} color={theme.palette.error?.main} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && (
          <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={25} rowsPerPageOptions={[25]} />
        )}
      </Paper>

      {/* Create Contact Dialog */}
      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setCreateError(""); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('mkt_add_contact')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {createError && (
              <Typography variant="body2" color="error" sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), px: 2, py: 1, borderRadius: 1 }}>
                {createError}
              </Typography>
            )}
            <TextField label={t('mkt_email')} type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} fullWidth size="small" required />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label={t('mkt_first_name')} value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} fullWidth size="small" />
              <TextField label={t('mkt_last_name')} value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} fullWidth size="small" />
            </Box>
            <Autocomplete
              options={orgSearchResults}
              getOptionLabel={(o) => o.name || ""}
              value={selectedOrg}
              onChange={(_, org) => {
                setSelectedOrg(org);
                setFormData({ ...formData, marketing_organization_id: org?.id || null, company: org?.name || formData.company });
              }}
              onInputChange={(_, value, reason) => {
                if (reason === "input") handleOrgSearch(value);
                if (reason === "clear") setOrgSearchResults([]);
              }}
              loading={orgSearchLoading}
              filterOptions={(x) => x}
              noOptionsText={t('mkt_type_to_search') || "Tapez pour rechercher..."}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('mkt_organisation')}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {orgSearchLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              size="small"
              fullWidth
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label={t('mkt_team')} value={formData.team} onChange={(e) => setFormData({ ...formData, team: e.target.value })} fullWidth size="small" />
              <TextField label={t('mkt_phone')} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} fullWidth size="small" />
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel>Segments</InputLabel>
              <Select multiple value={formData.segment_ids} onChange={(e) => setFormData({ ...formData, segment_ids: e.target.value })} label="Segments"
                renderValue={(sel) => sel.map((id) => (segments || []).find((s) => s.id === id)?.name || id).join(", ")}>
                {(segments || []).map((seg) => (
                  <MenuItem key={seg.id} value={seg.id}>
                    <Checkbox checked={formData.segment_ids.indexOf(seg.id) > -1} size="small" />
                    <ListItemText primary={seg.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>{t('common_cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!formData.email || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? t('mkt_adding') : t('mkt_add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t('mkt_contact_details')}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {!editing && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => setEditing(true)}>
                  <Edit2 size={16} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={() => setDetailOpen(false)} size="small"><X size={18} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedContact && !editing ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 700, fontSize: "1.1rem", flexShrink: 0,
                }}>
                  {(selectedContact.first_name?.[0] || selectedContact.email[0]).toUpperCase()}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {[selectedContact.first_name, selectedContact.last_name].filter(Boolean).join(" ") || "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{selectedContact.email}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>{t('mkt_organisation')}</Typography>
                  {selectedContact.organization_name ? (
                    <Chip
                      icon={<Building2 size={12} />}
                      label={selectedContact.organization_name}
                      size="small"
                      onClick={() => { setDetailOpen(false); navigate(`/admin/marketing/organisations/${selectedContact.marketing_organization_id}`); }}
                      sx={{ borderRadius: "6px", fontSize: "0.7rem", height: 22, mt: 0.5, cursor: "pointer",
                        bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}
                    />
                  ) : (
                    <Typography variant="body2">{selectedContact.company || "\u2014"}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>{t('mkt_team')}</Typography>
                  <Typography variant="body2">{selectedContact.team || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>{t('mkt_phone')}</Typography>
                  <Typography variant="body2">{selectedContact.phone || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>{t('mkt_lead_score')}</Typography>
                  <Chip icon={<Star size={12} />} label={selectedContact.lead_score} size="small" sx={{
                    borderRadius: "6px", fontSize: "0.7rem", height: 22, fontWeight: 600, mt: 0.5,
                    bgcolor: selectedContact.lead_score >= 50 ? alpha("#10b981", 0.1) : selectedContact.lead_score >= 20 ? alpha("#f59e0b", 0.1) : alpha(theme.palette.divider, 0.5),
                    color: selectedContact.lead_score >= 50 ? "#10b981" : selectedContact.lead_score >= 20 ? "#f59e0b" : theme.palette.text.secondary,
                  }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>{t('mkt_status')}</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {selectedContact.unsubscribed ? (
                      <Chip icon={<Ban size={12} />} label={t('mkt_unsubscribed')} size="small" color="error" sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }} />
                    ) : selectedContact.is_active === false ? (
                      <Chip
                        icon={<PowerOff size={11} />}
                        label={t("mkt_deactivated")}
                        size="small"
                        sx={{
                          borderRadius: "6px", fontSize: "0.65rem", height: 20, fontWeight: 600,
                          bgcolor: alpha(theme.palette.text.disabled, 0.15),
                          color: theme.palette.text.disabled,
                          "& .MuiChip-icon": { color: theme.palette.text.disabled },
                        }}
                      />
                    ) : (
                      <Chip icon={<CheckCircle2 size={11} />} label={t('mkt_active')} size="small" color="success" sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }} />
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>{t('analytics_source')}</Typography>
                  <Typography variant="body2">{selectedContact.source || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>{t('mkt_created')}</Typography>
                  <Typography variant="body2">{selectedContact.created_at ? new Date(selectedContact.created_at).toLocaleDateString() : "—"}</Typography>
                </Box>
              </Box>
              {(selectedContact.segments || []).length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem", mb: 0.5, display: "block" }}>{t('mkt_segments')}</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {selectedContact.segments.map((seg) => (
                      <Chip key={seg.id} label={seg.name} size="small" sx={{
                        borderRadius: "6px", fontSize: "0.65rem", height: 20,
                        bgcolor: seg.color ? alpha(seg.color, 0.1) : alpha(theme.palette.primary.main, 0.1),
                        color: seg.color || theme.palette.primary.main,
                      }} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : editData && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField label={t('mkt_email')} type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} fullWidth size="small" required />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField label={t('mkt_first_name')} value={editData.first_name} onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} fullWidth size="small" />
                <TextField label={t('mkt_last_name')} value={editData.last_name} onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} fullWidth size="small" />
              </Box>
              <Autocomplete
                options={orgSearchResults}
                getOptionLabel={(o) => o.name || ""}
                value={editSelectedOrg}
                onChange={(_, org) => {
                  setEditSelectedOrg(org);
                  setEditData({ ...editData, marketing_organization_id: org?.id || null, company: org?.name || editData.company });
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") handleOrgSearch(value);
                  if (reason === "clear") setOrgSearchResults([]);
                }}
                loading={orgSearchLoading}
                filterOptions={(x) => x}
                noOptionsText={t('mkt_type_to_search') || "Tapez pour rechercher..."}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('mkt_organisation')}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {orgSearchLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                size="small"
                fullWidth
                isOptionEqualToValue={(o, v) => o.id === v.id}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField label={t('mkt_team')} value={editData.team} onChange={(e) => setEditData({ ...editData, team: e.target.value })} fullWidth size="small" />
                <TextField label={t('mkt_phone')} value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} fullWidth size="small" />
              </Box>
              <FormControl size="small" fullWidth>
                <InputLabel>Segments</InputLabel>
                <Select multiple value={editData.segment_ids} onChange={(e) => setEditData({ ...editData, segment_ids: e.target.value })} label="Segments"
                  renderValue={(sel) => sel.map((id) => (segments || []).find((s) => s.id === id)?.name || id).join(", ")}>
                  {(segments || []).map((seg) => (
                  <MenuItem key={seg.id} value={seg.id}>
                    <Checkbox checked={editData.segment_ids.indexOf(seg.id) > -1} size="small" />
                    <ListItemText primary={seg.name} />
                  </MenuItem>
                ))}
                </Select>
              </FormControl>
              <Box
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: 1.5, py: 1, borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("mkt_active_for_campaigns")}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {editData.is_active
                      ? t("mkt_active_for_campaigns_help_active")
                      : t("mkt_active_for_campaigns_help_inactive")}
                  </Typography>
                </Box>
                <Switch
                  checked={editData.is_active}
                  onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                  color="success"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        {editing && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditing(false)}>{t('common_cancel')}</Button>
            <Button variant="contained" onClick={handleSaveContact} disabled={!editData?.email || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
              {saving ? t('mkt_saving') : t('common_save')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await dispatch(deleteMarketingContact(deleteTarget.id));
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.email}
        itemType={t("mkt_contact")}
      />

      {/* Timeline Dialog */}
      <Dialog open={timelineOpen} onClose={() => setTimelineOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between" }}>
          {t('mkt_timeline')} — {selectedContact?.email}
          <IconButton onClick={() => setTimelineOpen(false)} size="small"><X size={18} /></IconButton>
        </DialogTitle>
        <DialogContent>
          {(timeline?.items || []).length === 0 ? (
            <Typography sx={{ color: theme.palette.text.secondary, py: 3, textAlign: "center" }}>
              {t('mkt_no_events')}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
              {(timeline?.items || []).map((event) => (
                <Box key={event.id} sx={{ display: "flex", gap: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                  <Chip
                    label={event.event_type.replace(/_/g, " ")}
                    size="small"
                    sx={{ borderRadius: "6px", fontSize: "0.65rem", fontWeight: 600, minWidth: 100 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    {event.properties && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {JSON.stringify(event.properties)}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
                    {new Date(event.created_at).toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContactList;
